import { useMutation } from "@tanstack/react-query";
import {
  Modes,
  getEnumName,
  queryClient,
  service,
  useAuthConfig,
} from "@theprelude/core";
import {
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Loading,
  PauseIcon,
  PlayIcon,
  notifyError,
  notifySuccess,
} from "@theprelude/ds";
import { usePopper } from "@/hooks/use-popper";
import { scheduleQueryOptions } from "@/queries/schedule";

export function TogglePauseButton() {
  const dialog = usePopper();
  const authConfig = useAuthConfig();
  const scheduleQuery = scheduleQueryOptions(authConfig).useQuery();
  const allTestsPaused = scheduleQuery.data?.allTestsPaused ?? false;

  const pauseTests = useMutation({
    mutationFn: async () => {
      await service(authConfig).iam.updateAccount({
        mode: getEnumName(Modes, Modes.FROZEN),
      });
    },
    onSuccess: async () => {
      notifySuccess({
        id: "pause",
        message:
          "All tests have been paused. You can resume testing at any time.",
        dismissible: true,
      });
      dialog.close();
    },
    onError: async (error) => {
      dialog.close();
      notifyError({
        id: "pause",
        message: "Error occurred while trying to pause tests",
        error,
        dismissible: true,
      });
    },
    onSettled: async () => {
      void scheduleQueryOptions(authConfig).invalidate(queryClient);
    },
  });

  const { mutate: resumeAllTests, isPending: isResumingAllTests } = useMutation(
    {
      mutationFn: async () => {
        await service(authConfig).iam.updateAccount({
          mode: getEnumName(Modes, Modes.MANUAL),
        });
      },
      onSuccess: async () => {
        notifySuccess({
          message:
            "All tests have been resumed. You can pause testing at any time.",
          dismissible: true,
        });
      },
      onError: async (error) => {
        notifyError({
          message: "Error occurred while trying to resume tests",
          error,
          dismissible: true,
        });
      },
      onSettled: async () => {
        void scheduleQueryOptions(authConfig).invalidate(queryClient);
      },
    },
  );

  return (
    <>
      <Dialog {...dialog}>
        <DialogContent className="w-96">
          <DialogHeader>
            <DialogTitle>
              Are you sure you want to pause all running tests?
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            This will stop all tests from running. You can&#10;resume the paused
            events at any time.
          </DialogBody>
          <DialogFooter>
            <DialogClose asChild>
              <Button intent="secondary">Cancel</Button>
            </DialogClose>
            <Button
              intent="negative"
              loading={pauseTests.isPending}
              icon={<PauseIcon />}
              onClick={() => pauseTests.mutate()}
              className="dark:text-primary-background dark:hover:border-status-red dark:hover:bg-primary-background dark:hover:text-status-red"
            >
              Pause all tests
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {allTestsPaused ? (
        <Button
          icon={isResumingAllTests ? <Loading /> : <PlayIcon />}
          intent="positive"
          onClick={() => resumeAllTests()}
          className="border dark:text-primary-background dark:hover:border dark:hover:border-status-green dark:hover:bg-primary-background dark:hover:text-status-green"
        >
          Resume all tests
        </Button>
      ) : (
        <Button
          icon={<PauseIcon />}
          intent="negative-inverted"
          onClick={dialog.on}
        >
          Pause all tests
        </Button>
      )}
    </>
  );
}
